/* eslint-disable react-refresh/only-export-components */
// Charts — dependency-free, animated CSS/SVG chart components.
// Count-up numbers, SVG draw-on donut, animated bars + column timeline.
// Ported from the curro-redesign prototype (charts.jsx); rows come from the
// live /api/breakdown shape: [{ key, spend, lines }].

import { useEffect, useRef, useState } from "react";
import { fmtR, fmtRm, fmtN, PALETTE } from "../lib/format.js";

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia
  && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ---------------- in-view gate ----------------
   Charts hold their data animation (count-up, donut draw, bar/column grow)
   until the chart scrolls into view, a beat AFTER the section's reveal.js fade
   — so a below-the-fold chart doesn't quietly finish animating before the user
   ever reaches it. Above-the-fold charts pass the initial check and animate
   straight away.

   Uses the SAME scroll + getBoundingClientRect mechanism as reveal.js (rather
   than IntersectionObserver) so it can never get stuck: a synchronous mount
   check + scroll/resize listeners always fire, even in backgrounded/headless
   tabs where IO's intersection step is suspended. The trigger line (0.82·vh)
   is slightly tighter than reveal.js's fade line (0.9·vh), so the fade leads
   and the data animation follows. Under prefers-reduced-motion the gate is
   open from the first render (snap to final). Attach the returned ref to the
   chart's root element. */
const ANIM_DELAY = 260;          // ms after the chart enters view — lets the fade lead
const TRIGGER_FRACTION = 0.82;   // activate once the chart top is within 82% of the viewport

function useInView(delay = ANIM_DELAY) {
  const ref = useRef(null);
  const [active, setActive] = useState(prefersReducedMotion);   // reduced motion → open immediately
  useEffect(() => {
    if (active) return;
    const el = ref.current;
    if (!el) { setActive(true); return; }
    let timer, done = false;
    const vh = () => window.innerHeight || document.documentElement.clientHeight;
    const check = () => {
      if (done) return;
      const r = el.getBoundingClientRect();
      if (r.top < vh() * TRIGGER_FRACTION && r.bottom > 0) {
        done = true;
        window.removeEventListener("scroll", check);
        window.removeEventListener("resize", check);
        timer = setTimeout(() => setActive(true), delay);
      }
    };
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    check();   // synchronous initial check — activates above-the-fold charts on mount
    return () => { done = true; window.removeEventListener("scroll", check); window.removeEventListener("resize", check); clearTimeout(timer); };
  }, [active, delay]);
  return [ref, active];
}

/* count-up — holds at 0 until `active`, then eases to target (and animates
   between values on later data changes once active). */
export function useCountUp(target, dur = 900, active = true) {
  const [v, setV] = useState(0);
  const ref = useRef();
  useEffect(() => {
    if (!active) return;
    if (prefersReducedMotion()) { setV(target); return; }   // eslint-disable-line react-hooks/set-state-in-effect
    let raf, start;
    const from = ref.current ?? 0;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      setV(from + (target - from) * e);
      if (p < 1) raf = requestAnimationFrame(step);
      else ref.current = target;
    };
    raf = requestAnimationFrame(step);
    // safety net: if rAF is throttled (background tab), still land on target
    const safety = setTimeout(() => setV(target), dur + 400);
    return () => { cancelAnimationFrame(raf); clearTimeout(safety); };
  }, [target, dur, active]);
  return v;
}

export function CountUp({ value, fmt = (x) => Math.round(x).toLocaleString(), dur }) {
  const [ref, active] = useInView();
  const v = useCountUp(value, dur, active);
  return <span ref={ref}>{fmt(v)}</span>;
}

/* ---------------- Donut (SVG, draws on scroll-in) ---------------- */
export function Donut({ rows, topN = 8, size = 188, thickness = 26, centerTop, centerSub, palette = PALETTE }) {
  const [rootRef, on] = useInView();   // draw the arcs only once scrolled into view
  const [hover, setHover] = useState(null);

  const positive = rows.filter((r) => r.spend > 0);
  const total = positive.reduce((s, r) => s + r.spend, 0) || 1;
  const top = positive.slice(0, topN);
  const rest = positive.slice(topN);
  const items = [
    ...top.map((r, i) => ({ label: r.key, value: r.spend, color: palette[i % palette.length] })),
    ...(rest.length ? [{ label: `Others (${rest.length})`, value: rest.reduce((s, r) => s + r.spend, 0), color: "#9AA7C7" }] : []),
  ].map((s) => ({ ...s, pct: (s.value / total) * 100 }));

  if (items.length === 0) return null;

  const r = (size - thickness) / 2 - 6;
  const cx = size / 2;
  const C = 2 * Math.PI * r;
  let acc = 0;
  const segs = items.map((s) => {
    const len = (s.pct / 100) * C;
    const startDeg = (acc / 100) * 360;
    acc += s.pct;
    return { ...s, len, startDeg };
  });

  return (
    <div className="donut-row" ref={rootRef}>
      <div className="donut" style={{ width: size, maxWidth: "100%", aspectRatio: "1 / 1" }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={cx} cy={cx} r={r} fill="none" stroke="var(--track)" strokeWidth={thickness} />
          {segs.map((s, i) => (
            <circle key={i} cx={cx} cy={cx} r={r} fill="none" stroke={s.color}
              strokeWidth={hover === i ? thickness + 5 : thickness}
              strokeDasharray={`${s.len} ${C}`}
              strokeDashoffset={on ? 0 : s.len}
              strokeLinecap="butt"
              transform={`rotate(${s.startDeg} ${cx} ${cx})`}
              onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
              style={{ transition: `stroke-dashoffset .8s cubic-bezier(.22,.61,.36,1) ${i * 0.07}s, stroke-width .2s ease`, cursor: "default" }} />
          ))}
        </svg>
        <div className="center">
          <b>{hover != null ? `${segs[hover].pct.toFixed(1)}%` : centerTop}</b>
          <span>{hover != null ? segs[hover].label.slice(0, 16) : centerSub}</span>
        </div>
      </div>
      <ul className="legend">
        {segs.map((s, i) => (
          <li key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
            style={{ opacity: hover == null || hover === i ? 1 : 0.45, transition: "opacity .2s" }}>
            <i style={{ background: s.color }} />
            <span className="lname">{s.label}</span>
            <b>{fmtRm(s.value)}</b>
            <small>{s.pct.toFixed(1)}%</small>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------- Horizontal bars (grow on scroll-in) ---------------- */
export function HBars({ rows, topN = 10, fmt = fmtRm, palette = PALETTE, colorByIndex = false }) {
  const [rootRef, active] = useInView();
  const display = rows.slice(0, topN);
  const max = Math.max(1, ...display.map((r) => Math.abs(r.spend)));
  return (
    <div className="hbars" ref={rootRef}>
      {display.map((r, i) => {
        const w = (Math.abs(r.spend) / max) * 100;
        const fill = colorByIndex ? palette[i % palette.length]
          : i === 0 ? "linear-gradient(90deg,var(--curro-blue),var(--curro-sky))"
          : "linear-gradient(90deg,var(--curro-sky),#6FD0EE)";
        return (
          <div className="hbar" key={r.key}>
            <div className="nm">{r.key}</div>
            <div className="tk">
              <div className="fl" style={{ width: active ? `${w}%` : 0, background: fill,
                transition: `width .7s cubic-bezier(.22,.61,.36,1) ${i * 0.04}s` }} />
            </div>
            <div className="vl">{fmt(r.spend)}</div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- Column timeline ---------------- */
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthLabel = (k) => {
  const m = /^(\d{4})-(\d{2})/.exec(String(k));
  return m ? MONTH_NAMES[Number(m[2]) - 1] ?? k : String(k).slice(0, 3);
};

export function Timeline({ rows }) {
  const [rootRef, active] = useInView();
  const months = rows.filter((r) => r.key !== "(none)");
  const max = Math.max(1, ...months.map((r) => r.spend));
  const peak = Math.max(...months.map((r) => r.spend));
  if (months.length === 0) return null;
  return (
    <div ref={rootRef}>
      <div className="cols">
        {months.map((m, i) => {
          const h = Math.max(0, (m.spend / max) * 100);
          return (
            <div className="col" key={m.key} title={`${m.key}: ${fmtR(m.spend)} · ${fmtN(m.lines)} lines`}>
              <div className={`stem${m.spend === peak ? " peak" : ""}`}
                style={{ height: active ? `${h}%` : 0, transition: `height .7s cubic-bezier(.22,.61,.36,1) ${i * 0.04}s` }}>
                <span>{(m.spend / 1e6).toFixed(1)}</span>
              </div>
              <div className="lbl">{monthLabel(m.key)}</div>
            </div>
          );
        })}
      </div>
      <div className="axis" />
    </div>
  );
}

/* ============================================================
   A/B compare charts (Explore compare mode).
   Fills render at their FINAL width on first render (no mount-gated
   grow-from-zero); the CSS width transition only plays on later data /
   toggle changes — so a fill can't get stranded empty.
   ============================================================ */

/* paired A/B horizontal bars — A=blue, B=violet; top-N + exact Others roll-up */
export function PairedBars({ rows, topN = 12 }) {
  const sorted = [...rows].sort((x, y) => (Math.abs(y.a) + Math.abs(y.b)) - (Math.abs(x.a) + Math.abs(x.b)));
  const top = sorted.slice(0, topN);
  const rest = sorted.slice(topN);
  const items = [...top];
  if (rest.length) items.push({ key: `Others (${rest.length})`, others: true, a: rest.reduce((s, r) => s + r.a, 0), b: rest.reduce((s, r) => s + r.b, 0) });
  const max = Math.max(1, ...items.flatMap((r) => [Math.abs(r.a), Math.abs(r.b)]));
  const w = (v) => `${Math.min(100, (Math.abs(v) / max) * 100).toFixed(1)}%`;
  return (
    <div className="pbars">
      {items.map((r) => (
        <div className={`pbar-grp${r.others ? " others" : ""}`} key={r.key}>
          <div className="pbar-name" title={r.key}>{r.key}</div>
          <div className="pbar-tracks">
            <div className="pbar-row"><span className="pbar-tag a">A</span><div className="pbar-tk"><i className="a" style={{ width: w(r.a) }} /></div><span className="pbar-vl">{fmtRm(r.a)}</span></div>
            <div className="pbar-row"><span className="pbar-tag b">B</span><div className="pbar-tk"><i className="b" style={{ width: w(r.b) }} /></div><span className="pbar-vl">{fmtRm(r.b)}</span></div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* diverging Δ (B − A) — one bar per key, green right / red left of a zero centre,
   sorted by |Δ|. rows: [{ key, a, b, delta, pct }] */
export function DivergingDelta({ rows, topN = 18 }) {
  const sorted = [...rows].filter((r) => r.delta !== 0).sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta)).slice(0, topN);
  if (sorted.length === 0) return <p className="hint">No differences between the two views.</p>;
  const max = Math.max(1, ...sorted.map((r) => Math.abs(r.delta)));
  const w = (v) => `${Math.min(50, (Math.abs(v) / max) * 50).toFixed(2)}%`;
  return (
    <div className="dvg">
      <div className="dvg-zero-key"><span className="lo">← less in B</span><span className="hi">more in B →</span></div>
      {sorted.map((r) => {
        const pos = r.delta >= 0;
        return (
          <div className="dvg-row" key={r.key}>
            <div className="dvg-name" title={r.key}>{r.key}</div>
            <div className="dvg-track"><div className={`dvg-bar ${pos ? "pos" : "neg"}`} style={{ width: w(r.delta) }} /></div>
            <div className="dvg-fig" style={{ color: pos ? "var(--pos)" : "var(--neg)" }}>
              <span>{pos ? "+" : "−"}{fmtRm(Math.abs(r.delta))}</span>
              <small>{r.pct == null ? "new" : `${r.pct >= 0 ? "+" : ""}${r.pct.toFixed(0)}%`}</small>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* two donuts side by side (share-shift A vs B); rowsA/rowsB share a key order */
export function CompareDonuts({ rowsA, rowsB }) {
  const totA = rowsA.reduce((s, r) => s + Math.max(0, r.spend), 0);
  const totB = rowsB.reduce((s, r) => s + Math.max(0, r.spend), 0);
  return (
    <div className="cmp-donuts">
      <div className="cmp-donut-card">
        <div className="cd-head"><span className="cd-dot" style={{ background: "var(--curro-blue)" }} /> View A</div>
        <Donut rows={rowsA} topN={7} size={172} centerTop={fmtRm(totA)} centerSub="View A" />
      </div>
      <div className="cmp-donut-card">
        <div className="cd-head"><span className="cd-dot" style={{ background: "var(--curro-violet)" }} /> View B</div>
        <Donut rows={rowsB} topN={7} size={172} centerTop={fmtRm(totB)} centerSub="View B" />
      </div>
    </div>
  );
}

/* grouped column timeline — A + B series per month (run-vs-run / seasonality).
   Each column carries an interactive tooltip (month · A · B · Δ) that opens on
   hover (desktop) and tap (mobile) — native title= tooltips don't fire on touch. */
export function GroupedColumns({ rows, order }) {
  const ordered = order.map((k) => rows.find((r) => r.key === k)).filter(Boolean);
  const [active, setActive] = useState(null);
  if (ordered.length === 0) return null;
  const max = Math.max(1, ...ordered.flatMap((m) => [m.a, m.b]));
  const n = ordered.length;
  return (
    <div className="gcols">
      <div className="ab-legend"><span className="it"><span className="sw a" /> View A</span><span className="it"><span className="sw b" /> View B</span></div>
      <div className="gcols-plot" onMouseLeave={() => setActive(null)}>
        {ordered.map((m, i) => {
          const d = m.b - m.a;
          const pct = m.a ? (d / Math.abs(m.a)) * 100 : null;
          const pos = d >= 0;
          const edge = i <= 1 ? " start" : i >= n - 2 ? " end" : "";
          return (
            <div
              className={`gcol${active === i ? " active" : ""}`}
              key={m.key}
              onMouseEnter={() => setActive(i)}
              onClick={() => setActive((a) => (a === i ? null : i))}
            >
              <div className={`gcol-tip${edge}${active === i ? " on" : ""}`} role="tooltip">
                <b>{monthLabel(m.key)}</b>
                <span><i className="sw a" />A&nbsp;&nbsp;<em>{fmtR(m.a)}</em></span>
                <span><i className="sw b" />B&nbsp;&nbsp;<em>{fmtR(m.b)}</em></span>
                <span className="dlt" style={{ color: pos ? "var(--pos)" : "var(--neg)" }}>
                  {pos ? "+" : "−"}{fmtRm(Math.abs(d))}{pct == null ? "" : ` · ${pos ? "+" : ""}${pct.toFixed(0)}%`}
                </span>
              </div>
              <div className="gcol-pair">
                <div className="gcol-stem a" style={{ height: `${(m.a / max) * 100}%` }} />
                <div className="gcol-stem b" style={{ height: `${(m.b / max) * 100}%` }} />
              </div>
              <div className="gcol-lbl">{monthLabel(m.key)}</div>
            </div>
          );
        })}
      </div>
      <div className="axis" />
    </div>
  );
}

/* ============================================================
   Quality-report charts (Pipeline modals).
   NB: every bar/gauge fill renders at its FINAL width on first
   render — NOT animated from 0 — so a frozen animation clock can
   never leave a fill stuck empty (static-correct > animated-broken).
   ============================================================ */

/* ---------------- Pass-rate ring ---------------- */
export function PassGauge({ passed, total }) {
  const size = 168, thickness = 17, cx = size / 2;
  const r = (size - thickness) / 2 - 2;
  const C = 2 * Math.PI * r;
  const pct = total ? passed / total : 0;
  const fails = total - passed;
  return (
    <div className="qr-ring">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={cx} cy={cx} r={r} fill="none" stroke="var(--track)" strokeWidth={thickness} />
        {fails > 0 && (
          <circle cx={cx} cy={cx} r={r} fill="none" stroke="var(--neg)" strokeWidth={thickness}
            strokeDasharray={`${(fails / total) * C} ${C}`}
            transform={`rotate(${pct * 360} ${cx} ${cx})`} strokeLinecap="butt" />
        )}
        <circle cx={cx} cy={cx} r={r} fill="none" stroke="var(--pos)" strokeWidth={thickness} strokeLinecap="round"
          strokeDasharray={`${pct * C} ${C}`} />
      </svg>
      <div className="qr-ring-c">
        <b className="num">{passed}<span>/{total}</span></b>
        <span>checks passed</span>
      </div>
    </div>
  );
}

/* ---------------- Reconciliation deviation chart ----------------
   One compact row per check: a centre line = the expected target, and a dot
   showing how far the computed value deviates (signed %). Exact matches sit on
   the line (green) — the whole column reads "fully reconciled" at a glance; a
   failure pops off-centre in red. Far denser than per-check bullet bars, and
   the exact R-value stays on the right (+ computed-vs-expected on hover). */
export function ReconDeviation({ items }) {
  const fmtVal = (v, fmt) => (fmt === "money" ? fmtR(v) : fmtN(v));
  const SCALE = 8;   // ±% visual scale; larger deviations clamp to the edges
  return (
    <div className="recon-dev">
      <div className="recon-dev-legend"><span>under</span><span>on target = reconciled</span><span>over</span></div>
      {items.map((c, i) => {
        const delta = c.expectedVal ? ((c.actualVal - c.expectedVal) / c.expectedVal) * 100 : (c.actualVal === 0 ? 0 : 100);
        const exact = Math.abs(delta) < 0.05;
        const pos = 50 + (Math.max(-SCALE, Math.min(SCALE, delta)) / SCALE) * 50;   // 0–100%
        const color = c.pass ? "var(--pos)" : "var(--neg)";
        const title = `${c.name}: computed ${fmtVal(c.actualVal, c.fmt)} vs expected ${fmtVal(c.expectedVal, c.fmt)}`
          + (exact ? " (exact)" : ` (${delta > 0 ? "+" : ""}${delta.toFixed(2)}%)`);
        return (
          <div className="recon-dev-row" key={i} title={title}>
            <div className="rd-name">{c.name}</div>
            <div className="rd-track">
              <div className="axis" />
              <div className="centre" />
              {!exact && <div className="conn" style={{ left: `${Math.min(50, pos)}%`, width: `${Math.abs(pos - 50)}%`, background: color }} />}
              <div className="dot" style={{ left: `${pos}%`, background: color }} />
            </div>
            <div className="rd-val" style={{ color }}>
              {fmtVal(c.actualVal, c.fmt)}
              {!c.pass && <small>{delta > 0 ? "+" : ""}{delta.toFixed(2)}%</small>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- Tolerance gauges ---------------- */
export function ToleranceGauges({ items }) {
  return (
    <div className="qr-gauges">
      {items.map((c, i) => {
        const mag = c.signed ? Math.abs(c.value) : c.value;
        const scaleMax = Math.max(c.limit * 1.35, mag * 1.2) || 1;
        const fillPct = Math.min(100, (mag / scaleMax) * 100);
        const limitPct = (c.limit / scaleMax) * 100;
        const ok = mag <= c.limit;
        return (
          <div className="qr-gauge" key={i}>
            <div className="qr-gauge-head">
              <span>{c.name}</span>
              <b className="num" style={{ color: ok ? "var(--pos)" : "var(--neg)" }}>
                {c.signed && c.value > 0 ? "+" : ""}{c.value.toFixed(1)}%
              </b>
            </div>
            <div className="qr-gauge-track">
              <div className="fl" style={{ width: `${Math.max(2, fillPct)}%`, background: ok ? "linear-gradient(90deg,var(--curro-blue),var(--curro-sky))" : "var(--neg)" }} />
              <div className="lim" style={{ left: `${Math.max(0, Math.min(100, limitPct))}%` }} />
            </div>
            <div className="qr-gauge-foot">ceiling {c.limit}%</div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- Column-health vertical bar chart ----------------
   Used by BOTH the Quality-report modal and the "Look inside" drill
   modal. Tooltip background is --curro-navy (theme-independent dark) —
   never --ink, which inverts light in dark mode and would hide the
   white tooltip text. */
export function ColumnHealthBars({ columns }) {
  const [hover, setHover] = useState(null);
  const cols = [...columns].sort((a, b) => b.nullPct - a.nullPct);
  if (cols.length === 0) return null;
  const W = 1000, H = 320, padL = 48, padR = 16, padT = 20, padB = 74;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const band = plotW / cols.length, barW = Math.min(54, band * 0.52);
  const scaleMax = Math.max(Math.max(...cols.map((c) => c.nullPct), 1) * 1.2, 4);
  const x = (i) => padL + band * (i + 0.5);
  const y = (v) => padT + plotH - (v / scaleMax) * plotH;
  const ticks = [0, scaleMax / 2, scaleMax];
  const barColor = (p) => (p > 20 ? "var(--neg)" : p >= 5 ? "var(--curro-yellow)" : "var(--curro-blue)");
  return (
    <div className="qr-colchart">
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: 300, display: "block", overflow: "visible" }} onMouseLeave={() => setHover(null)}>
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} stroke="var(--line)" strokeWidth="1" />
            <text x={padL - 9} y={y(t) + 4} textAnchor="end" fontSize="12.5" fill="var(--muted)" fontFamily="var(--font-num)">{t.toFixed(0)}%</text>
          </g>
        ))}
        {cols.map((c, i) => {
          const h = (c.nullPct / scaleMax) * plotH;
          const on = hover === i;
          return (
            <g key={c.name} onMouseEnter={() => setHover(i)}>
              <rect x={x(i) - band / 2} y={padT} width={band} height={plotH} fill="transparent" />
              <rect x={x(i) - barW / 2} y={y(c.nullPct)} width={barW} height={Math.max(2, h)} rx="5"
                fill={barColor(c.nullPct)} opacity={hover == null || on ? 1 : 0.5}
                style={{ transition: "opacity .2s" }} />
              <text x={x(i)} y={y(c.nullPct) - 8} textAnchor="middle" fontSize="12.5" fontFamily="var(--font-num)" fontWeight="700"
                fill="var(--ink)" opacity={hover == null || on ? 1 : 0.5}>{c.nullPct.toFixed(1)}%</text>
              <text x={x(i)} y={H - padB + 18} textAnchor="end" fontSize="12" fill={on ? "var(--ink)" : "var(--muted)"}
                fontFamily="var(--font-num)" transform={`rotate(-32 ${x(i)} ${H - padB + 18})`}>{c.name}</text>
            </g>
          );
        })}
        {hover != null && (
          <g transform={`translate(${Math.min(Math.max(x(hover), padL + 78), W - padR - 78)}, ${Math.max(y(cols[hover].nullPct) - 62, padT)})`}>
            <rect x="-78" y="0" width="156" height="48" rx="9" fill="var(--curro-navy)" opacity="0.97" />
            <text x="0" y="19" textAnchor="middle" fontSize="13" fontWeight="700" fill="#fff" fontFamily="var(--font-head)">{cols[hover].name}</text>
            <text x="0" y="36" textAnchor="middle" fontSize="12" fill="#7FD3F0" fontFamily="var(--font-num)">{cols[hover].nullPct.toFixed(1)}% empty · {fmtN(cols[hover].distinct)} distinct</text>
          </g>
        )}
      </svg>
    </div>
  );
}
